import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, MapPin, Heart, Trash2, ArrowLeft, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyFasts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingIds, setDeletingIds] = useState(new Set());

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCommitments();
  }, [user, navigate]);

  const fetchCommitments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/commitments/my`);
      setCommitments(response.data);
    } catch (error) {
      console.error('Failed to fetch commitments:', error);
      toast.error('Failed to load your fasts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (commitmentId) => {
    if (!window.confirm('Are you sure you want to remove this fasting commitment?')) {
      return;
    }

    // Optimistic update - immediately remove from UI
    setDeletingIds(prev => new Set([...prev, commitmentId]));
    const previousCommitments = [...commitments];
    setCommitments(prev => prev.filter(c => c.id !== commitmentId));
    
    try {
      await axios.delete(`${API_URL}/api/commitments/${commitmentId}`);
      toast.success('Commitment removed');
    } catch (error) {
      // Revert on error
      setCommitments(previousCommitments);
      toast.error('Failed to remove commitment');
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(commitmentId);
        return newSet;
      });
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isUpcoming = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr + 'T00:00:00');
    return date >= today;
  };

  const upcomingFasts = commitments.filter(c => isUpcoming(c.date));
  const pastFasts = commitments.filter(c => !isUpcoming(c.date));

  if (!user) return null;

  return (
    <div className="min-h-screen pt-20 pb-12 px-4" data-testid="my-fasts-page">
      <div className="grain-overlay" />
      
      <div className="max-w-3xl mx-auto animate-fade-in-up">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="mb-8">
          <h1 className="font-display text-3xl text-foreground mb-2">My Fasting Journey</h1>
          <p className="text-muted-foreground">
            Track your commitments and reflect on your journey
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading your fasts...</p>
          </div>
        ) : commitments.length === 0 ? (
          <Card className="bg-white/60 backdrop-blur-sm border-border/50 text-center py-12">
            <CardContent>
              <Calendar className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="font-display text-xl mb-2">No fasts yet</h3>
              <p className="text-muted-foreground mb-6">
                You haven't committed to any fasts yet. Choose a date from the calendar to begin.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-primary hover:bg-primary/90 text-white rounded-full"
                data-testid="view-calendar-btn"
              >
                View Calendar
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Upcoming Fasts */}
            {upcomingFasts.length > 0 && (
              <div>
                <h2 className="font-display text-xl text-foreground mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Upcoming Fasts ({upcomingFasts.length})
                </h2>
                <div className="space-y-4">
                  {upcomingFasts.map((commitment, index) => (
                    <Card 
                      key={commitment.id}
                      className={`bg-white/60 backdrop-blur-sm border-border/50 animate-fade-in-up transition-all ${
                        deletingIds.has(commitment.id) ? 'opacity-50 scale-98' : ''
                      }`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                      data-testid={`upcoming-fast-${commitment.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-display text-lg text-primary mb-1">
                              {formatDate(commitment.date)}
                            </p>
                            <p className="text-foreground/80 mb-2">"{commitment.reason}"</p>
                            
                            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                              {commitment.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3.5 w-3.5" />
                                  {commitment.location}
                                </span>
                              )}
                              {commitment.charity_name && (
                                <span className="flex items-center gap-1 text-primary">
                                  <Heart className="h-3.5 w-3.5" />
                                  {commitment.charity_name}
                                </span>
                              )}
                            </div>
                            
                            {commitment.reason_categories.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-3">
                                {commitment.reason_categories.map(cat => (
                                  <span 
                                    key={cat}
                                    className="text-xs bg-secondary px-2 py-0.5 rounded-full text-secondary-foreground"
                                  >
                                    {cat.replace('_', ' ')}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(commitment.id)}
                            disabled={deletingIds.has(commitment.id)}
                            className="text-muted-foreground hover:text-destructive"
                            data-testid={`delete-fast-${commitment.id}`}
                          >
                            {deletingIds.has(commitment.id) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Past Fasts */}
            {pastFasts.length > 0 && (
              <div>
                <h2 className="font-display text-xl text-foreground mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  Past Fasts ({pastFasts.length})
                </h2>
                <div className="space-y-3">
                  {pastFasts.map((commitment, index) => (
                    <Card 
                      key={commitment.id}
                      className="bg-secondary/30 backdrop-blur-sm border-border/30 animate-fade-in-up"
                      style={{ animationDelay: `${index * 0.05}s` }}
                      data-testid={`past-fast-${commitment.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-foreground/80">
                              {formatDate(commitment.date)}
                            </p>
                            <p className="text-sm text-muted-foreground truncate max-w-md">
                              "{commitment.reason}"
                            </p>
                          </div>
                          {commitment.charity_name && (
                            <span className="text-xs text-primary flex items-center gap-1">
                              <Heart className="h-3 w-3" />
                              {commitment.charity_name}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyFasts;
